import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card,
  StatCard,
  LoadingSpinner,
  Grid2,
  Grid4,
  MetricWithTooltip,
  IconButton,
  DatabaseSelector,
} from '../components/ui';
import { Accordion } from '../components/ui/Accordion';
import FilterableTable from '../components/FilterableTable';
import TimeSeriesChart from '../components/charts/TimeSeriesChart';
import MaxDiffChartsSection from '../components/charts/MaxDiffChartsSection';
import TimeSeriesFilters from '../components/TimeSeriesFilters';
import {
  ArrowPathIcon, PlusIcon,
} from '@heroicons/react/24/outline';
import { useDatabase } from '../contexts/DatabaseContext';
import {
  useAnalyticsOverview,
  useIndustryAnalytics,
  useRegionAnalytics,
  useVillageAnalytics,
  useSectorAnalytics,
  useMinistryAnalytics,
  useCompanies,
  useTimeSeriesAnalytics,
} from '../hooks/useAnalytics';

const legacyMetricDefinitions = {
  totalCompanies:
    'The total number of unique companies that have participated in the survey.',
  totalSurveyResponses:
    'The total number of individual survey responses collected across all companies.',
  avgAdoptionRate:
    'The percentage of employees, across all companies, who have used at least one AI tool in their work.',
  highAdoptersCompany:
    'The total number of companies with an average employee adoption rate of 60% or more.',
  avgATIScore:
    'The average AI-Technology Interaction (ATI) score across all employees, measuring overall affinity and positive interaction with technology (0-100).',
  highAffinity:
    'The percentage of all employees who have a "high" ATI score (typically 75 or above).',
  avgAIKnowledge:
    'The average score on the AI literacy quiz across all employees (out of 5).',
  avgAdoptionLevel:
    'The average self-reported usage of AI tools by all employees on a scale of 0-100.',
  highAdoptersPercent:
    'The total number and percentage of companies with an average employee adoption rate of 60% or more.',
  avgResponsesPerCompany:
    'The average number of survey responses received per company.',
  avgOptimismScore:
    'The average sentiment towards AI\'s future impact, measured across all employees (0-10).',
  avgAITrust:
    'The average self-reported trust in the accuracy and reliability of AI systems, across all employees (0-10).',
  avgAIFear:
    'The average self-reported concern or fear about AI\'s negative impacts, across all employees (0-10).',
  learningInterest:
    'The percentage of all employees who expressed active interest in AI training.',
  adoptionRate:
    'The average percentage of employees in this group who have used at least one AI tool.',
  adoptionLevel:
    'The average self-reported usage of AI tools by employees in this group (0-100).',
  atiScore:
    'The average AI-Technology Interaction (ATI) score for employees in this group (0-100).',
  aiKnowledge:
    'The average score on the AI literacy quiz for employees in this group (out of 5).',
};

const legacyAvailableMetrics = [
  { key: 'adoptionRate', name: 'Adoption Rate', color: '#10b981' },
  { key: 'atiScore', name: 'ATI Score', color: '#8b5cf6' },
  { key: 'aiKnowledge', name: 'AI Knowledge', color: '#3b82f6' },
  { key: 'optimismScore', name: 'Optimism', color: '#f59e0b' },
  { key: 'trust', name: 'AI Trust', color: '#06b6d4' },
  { key: 'fear', name: 'AI Fear', color: '#ef4444' },
  { key: 'learningInterest', name: 'Learning Interest', color: '#ec4899' }
];

const DEFAULT_COLORS = [
  '#10b981', '#8b5cf6', '#3b82f6', '#f59e0b', '#06b6d4', '#ef4444', '#ec4899', '#14b8a6'
];

function formatMetricValue(value, metricDef) {
  if (value == null || isNaN(value)) return '—';
  const decimals = metricDef?.decimals ?? 1;
  const formatted = Number(value).toFixed(decimals);
  const unit = metricDef?.unit || '';
  if (unit === '%') return `${formatted}%`;
  return `${formatted}${unit}`;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { databaseId, schema } = useDatabase();
  const [activeTab, setActiveTab] = useState('overview');

  // Determine if we're in dynamic mode (database schema available)
  const isDynamic = !!databaseId && !!schema;
  const schemaMetrics = schema?.metrics || [];
  const layout = schema?.dashboardLayout || null;

  // Build metric lookup from schema
  const metricLookup = useMemo(() => {
    const map = {};
    schemaMetrics.forEach(m => { map[m.key] = m; });
    return map;
  }, [schemaMetrics]);

  // Dynamic metric definitions (used for tooltips)
  const metricDefinitions = useMemo(() => {
    if (!isDynamic) return legacyMetricDefinitions;
    const defs = { ...legacyMetricDefinitions };
    schemaMetrics.forEach(m => {
      defs[m.key] = m.label + (m.unit ? ` (${m.unit})` : '');
    });
    return defs;
  }, [isDynamic, schemaMetrics]);

  // Available metrics for time series
  const availableMetrics = useMemo(() => {
    if (!isDynamic) return legacyAvailableMetrics;
    if (layout?.timeSeriesMetrics) {
      return layout.timeSeriesMetrics.map((tsm, i) => {
        const def = metricLookup[tsm.metricKey];
        return {
          key: tsm.metricKey,
          name: def?.label || tsm.metricKey,
          color: tsm.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
        };
      });
    }
    // Fallback: derive from schema metrics when the layout doesn't define them
    return schemaMetrics.map((m, i) => ({
      key: m.key,
      name: m.label || m.key,
      color: DEFAULT_COLORS[i % DEFAULT_COLORS.length],
    }));
  }, [isDynamic, layout, metricLookup, schemaMetrics]);

  // Build dynamic TimeSeriesChart config from schema
  const dynamicTimeSeriesConfigs = useMemo(() => {
    if (!isDynamic) return null;
    const configs = {};
    const tsMetrics = layout?.timeSeriesMetrics
      ? layout.timeSeriesMetrics.map((tsm) => ({ metricKey: tsm.metricKey, color: tsm.color }))
      : schemaMetrics.map((m) => ({ metricKey: m.key }));
    tsMetrics.forEach((tsm, i) => {
      const def = metricLookup[tsm.metricKey];
      const color = tsm.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length];
      configs[tsm.metricKey] = {
        key: tsm.metricKey,
        name: def?.label || tsm.metricKey,
        color,
        yAxisId: 'metrics',
        stdKey: `std_${tsm.metricKey}`,
      };
    });
    configs.responseCount = {
      key: 'cumulativeResponses',
      name: 'Total Responses',
      color: '#6b7280',
      yAxisId: 'responses',
    };
    return configs;
  }, [isDynamic, layout, metricLookup, schemaMetrics]);

  // Helper to read metric value from company doc
  const getMetric = (company, key) => {
    if (isDynamic && databaseId) {
      return company.databases?.[databaseId]?.metrics?.[key] ?? company.metrics?.[key] ?? 0;
    }
    return company.metrics?.[key] ?? 0;
  };

  const getResponseCount = (company) => {
    if (isDynamic && databaseId) {
      return company.databases?.[databaseId]?.responseCount ?? company.responseCount ?? 0;
    }
    return company.responseCount ?? 0;
  };

  // Breakdown table metric columns (first 3 comparison metrics from layout, or legacy)
  const breakdownMetricColumns = useMemo(() => {
    if (!isDynamic || !layout?.comparisonMetrics) return null;
    return layout.comparisonMetrics.slice(0, 3).map((key, i) => {
      const def = metricLookup[key];
      return {
        key,
        label: def?.label || key,
        unit: def?.unit || '',
        decimals: def?.decimals ?? 1,
        color: ['text-green-600 dark:text-success', 'font-semibold text-purple-600', 'font-semibold text-blue-600'][i] || 'font-semibold',
      };
    });
  }, [isDynamic, layout, metricLookup]);

  // Time series tabs state
  const defaultMetrics = availableMetrics.length >= 2
    ? [availableMetrics[0].key, availableMetrics[1].key]
    : availableMetrics.length >= 1 ? [availableMetrics[0].key] : [];

  const [timeSeriesTabs, setTimeSeriesTabs] = useState([
    {
      id: 1,
      name: 'Overview',
      filters: {
        industry: 'all',
        village: 'all',
        region: 'all',
        sector: 'all',
        ministry: 'all'
      },
      activeMetrics: ['adoptionRate', 'atiScore'],
      showStdDev: true
    }
  ]);
  const [activeTimeSeriesTab, setActiveTimeSeriesTab] = useState(0);

  // Get current tab's settings
  const currentTab = timeSeriesTabs[activeTimeSeriesTab] || timeSeriesTabs[0];
  const timeSeriesFilters = currentTab.filters;
  const activeTimeSeriesMetrics = currentTab.activeMetrics;
  const showStdDev = currentTab.showStdDev;

  // Update current tab's settings
  const updateCurrentTab = (updates) => {
    setTimeSeriesTabs(prev => prev.map((tab, idx) =>
      idx === activeTimeSeriesTab ? { ...tab, ...updates } : tab
    ));
  };

  const setTimeSeriesFilters = (filters) => updateCurrentTab({ filters });
  const setActiveTimeSeriesMetrics = (metrics) => updateCurrentTab({ activeMetrics: metrics });
  const setShowStdDev = (show) => updateCurrentTab({ showStdDev: show });

  // Tab management functions
  const addTimeSeriesTab = () => {
    const newTab = {
      id: Date.now(),
      name: `Chart ${timeSeriesTabs.length + 1}`,
      filters: {
        industry: 'all',
        village: 'all',
        region: 'all',
        sector: 'all',
        ministry: 'all'
      },
      activeMetrics: defaultMetrics,
      showStdDev: true
    };
    setTimeSeriesTabs([...timeSeriesTabs, newTab]);
    setActiveTimeSeriesTab(timeSeriesTabs.length);
  };

  const removeTimeSeriesTab = (index) => {
    if (timeSeriesTabs.length === 1) return;
    const newTabs = timeSeriesTabs.filter((_, idx) => idx !== index);
    setTimeSeriesTabs(newTabs);
    if (activeTimeSeriesTab >= newTabs.length) {
      setActiveTimeSeriesTab(newTabs.length - 1);
    } else if (activeTimeSeriesTab > index) {
      setActiveTimeSeriesTab(activeTimeSeriesTab - 1);
    }
  };

  // Generate filter summary text
  const getFilterSummary = () => {
    const parts = [];

    if (timeSeriesFilters.industry !== 'all') {
      parts.push(`${timeSeriesFilters.industry} industry`);
    }
    if (timeSeriesFilters.village !== 'all') {
      parts.push(`${timeSeriesFilters.village} village`);
    }
    if (timeSeriesFilters.region !== 'all') {
      parts.push(`${timeSeriesFilters.region} region`);
    }
    if (timeSeriesFilters.sector !== 'all') {
      parts.push(`${timeSeriesFilters.sector} sector`);
    }
    if (timeSeriesFilters.ministry !== 'all') {
      parts.push(`${timeSeriesFilters.ministry}`);
    }

    if (parts.length === 0) {
      return 'Showing all data across all companies';
    } else if (parts.length === 1) {
      return `Filtered by ${parts[0]}`;
    } else if (parts.length === 2) {
      return `Filtered by ${parts[0]} and ${parts[1]}`;
    } else {
      const lastPart = parts.pop();
      return `Filtered by ${parts.join(', ')}, and ${lastPart}`;
    }
  };

  // Use React Query hooks for cached data fetching (pass databaseId when available)
  const {
    data: analytics,
    isLoading: analyticsLoading,
    isError: analyticsError,
    refetch: refetchAnalytics
  } = useAnalyticsOverview(databaseId);

  const {
    data: companiesData = [],
    isLoading: companiesLoading,
    refetch: refetchCompanies
  } = useCompanies({ databaseId });

  const {
    data: industryData = [],
    isLoading: industryLoading,
    refetch: refetchIndustry
  } = useIndustryAnalytics(databaseId);

  const {
    data: regionData = [],
    isLoading: regionLoading,
    refetch: refetchRegion
  } = useRegionAnalytics(databaseId);

  const {
    data: villageData = [],
    isLoading: villageLoading,
    refetch: refetchVillage
  } = useVillageAnalytics(databaseId);

  const {
    data: sectorData = [],
    isLoading: sectorLoading,
    refetch: refetchSector
  } = useSectorAnalytics(databaseId);

  const {
    data: ministryData = [],
    isLoading: ministryLoading,
    refetch: refetchMinistry
  } = useMinistryAnalytics(databaseId);

  const {
    data: timeSeriesData = [],
    isLoading: timeSeriesLoading,
    refetch: refetchTimeSeries
  } = useTimeSeriesAnalytics({
    binSize: 'day',
    databaseId,
    ...timeSeriesFilters
  });

  // Combine loading and error states
  const loading = analyticsLoading || companiesLoading || industryLoading || regionLoading || villageLoading || sectorLoading || ministryLoading;
  const error = analyticsError;

  // Refetch all data function
  const fetchData = () => {
    refetchAnalytics();
    refetchCompanies();
    refetchIndustry();
    refetchRegion();
    refetchVillage();
    refetchSector();
    refetchMinistry();
    refetchTimeSeries();
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Extract unique values for filters from companies data
  const uniqueIndustries = [...new Set(companiesData.map(c => c.industry).filter(Boolean))].sort();
  const uniqueVillages = [...new Set(companiesData.map(c => c.village).filter(Boolean))].sort();
  const uniqueRegions = [...new Set(companiesData.map(c => c.region).filter(Boolean))].sort();
  const uniqueMinistries = [...new Set(companiesData.filter(c => c.isGovernment).map(c => c.ministry).filter(Boolean))].sort();

  // Analytics section readiness
  const analyticsReady = !loading && !error && analytics && analytics.totalCompanies > 0;
  const analyticsNoData = !loading && !error && (!analytics || analytics.totalCompanies === 0);

  // Prepare data for companies table
  const companiesTableData = companiesData
    .sort((a, b) => {
      if (isDynamic && schemaMetrics.length > 0) {
        const sortKey = schemaMetrics[0].key;
        return getMetric(b, sortKey) - getMetric(a, sortKey);
      }
      return (b.metrics?.adoptionRate ?? 0) - (a.metrics?.adoptionRate ?? 0);
    })
    .map((company, index) => ({
      ...company,
      rank: index + 1,
      _getMetric: (key) => getMetric(company, key),
      onClick: () => navigate(`/insights?view=company&id=${company.id}${databaseId ? `&db=${schema?.slug || ''}` : ''}`)
    }));

  // Get unique industries for filtering
  const industries = [...new Set(companiesData.map(c => c.industry))].map(i => ({ value: i, label: i }));

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Dynamic company table columns
  const companyTableColumns = useMemo(() => {
    const base = [
      {
        key: 'rank',
        header: 'Rank',
        align: 'left',
        className: 'font-semibold text-brand-primary',
        render: (company) => `#${company.rank}`,
        sortValue: (company) => -company.rank
      },
      {
        key: 'name',
        header: 'Company',
        align: 'left',
        className: 'text-text-primary-light dark:text-text-primary'
      },
      {
        key: 'industry',
        header: 'Industry',
        align: 'left',
        className: 'text-text-secondary-light dark:text-text-secondary'
      },
    ];

    if (isDynamic && breakdownMetricColumns) {
      return [...base, ...breakdownMetricColumns.map(col => ({
        key: col.key,
        header: (
          <MetricWithTooltip
            label={col.label}
            definition={metricDefinitions[col.key] || col.label}
          />
        ),
        align: 'right',
        className: col.color,
        render: (company) => formatMetricValue(company._getMetric(col.key), metricLookup[col.key]),
        sortValue: (company) => company._getMetric(col.key),
      }))];
    }

    // Legacy columns
    return [...base,
      {
        key: 'adoptionRate',
        header: (
          <MetricWithTooltip
            label="Adoption Rate"
            definition={metricDefinitions.adoptionRate}
          />
        ),
        align: 'right',
        className: 'font-semibold text-green-600 dark:text-success',
        render: (company) => `${(company.metrics?.adoptionRate ?? 0).toFixed(1)}%`,
        sortValue: (company) => company.metrics?.adoptionRate ?? 0
      },
      {
        key: 'atiScore',
        header: (
          <MetricWithTooltip
            label="ATI Score"
            definition={metricDefinitions.atiScore}
          />
        ),
        align: 'right',
        className: 'font-semibold text-purple-600',
        render: (company) => `${(company.metrics?.avgATIScore ?? 0).toFixed(1)}/100`,
        sortValue: (company) => company.metrics?.avgATIScore ?? 0
      },
      {
        key: 'aiKnowledge',
        header: (
          <MetricWithTooltip
            label="AI Knowledge"
            definition={metricDefinitions.aiKnowledge}
          />
        ),
        align: 'right',
        className: 'font-semibold text-blue-600',
        render: (company) => `${(company.metrics?.avgAIKnowledge ?? 0).toFixed(2)}/5`,
        sortValue: (company) => company.metrics?.avgAIKnowledge ?? 0
      }
    ];
  }, [isDynamic, breakdownMetricColumns, metricDefinitions, metricLookup]);

  // Helper to generate breakdown table columns for a given group key
  const makeBreakdownColumns = (groupKey, groupLabel) => {
    const base = [
      {
        key: groupKey,
        header: groupLabel,
        align: 'left',
        className: 'font-semibold text-text-primary-light dark:text-text-primary'
      },
      {
        key: 'companyCount',
        header: 'Companies',
        align: 'right',
        className: 'text-text-secondary-light dark:text-text-secondary'
      },
      {
        key: 'totalResponses',
        header: 'Responses',
        align: 'right',
        className: 'text-text-secondary-light dark:text-text-secondary',
        render: (item) => formatNumber(item.totalResponses),
        sortValue: (item) => item.totalResponses
      },
    ];

    if (isDynamic && breakdownMetricColumns) {
      return [...base, ...breakdownMetricColumns.map(col => ({
        key: col.key,
        header: (
          <MetricWithTooltip
            label={col.label}
            definition={metricDefinitions[col.key] || col.label}
          />
        ),
        align: 'right',
        className: col.color,
        render: (item) => formatMetricValue(item.metrics?.[col.key], metricLookup[col.key]),
        sortValue: (item) => item.metrics?.[col.key] ?? 0,
      }))];
    }

    // Legacy columns
    return [...base,
      {
        key: 'averageAdoptionRate',
        header: (
          <MetricWithTooltip
            label="Adoption Rate"
            definition={metricDefinitions.adoptionRate}
          />
        ),
        align: 'right',
        className: 'font-semibold text-green-600 dark:text-success',
        render: (item) => `${item.averageAdoptionRate.toFixed(1)}%`,
        sortValue: (item) => item.averageAdoptionRate
      },
      {
        key: 'averageATIScore',
        header: (
          <MetricWithTooltip
            label="ATI Score"
            definition={metricDefinitions.atiScore}
          />
        ),
        align: 'right',
        className: 'font-semibold text-purple-600',
        render: (item) => `${item.averageATIScore.toFixed(1)}/100`,
        sortValue: (item) => item.averageATIScore
      },
      {
        key: 'averageKnowledge',
        header: (
          <MetricWithTooltip
            label="AI Knowledge"
            definition={metricDefinitions.aiKnowledge}
          />
        ),
        align: 'right',
        className: 'font-semibold text-blue-600',
        render: (item) => `${item.averageKnowledge.toFixed(2)}/5`,
        sortValue: (item) => item.averageKnowledge
      }
    ];
  };

  return (
    <>
    <div className="container mx-auto px-4 py-10">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-text-primary-light dark:text-text-primary">
              Dashboard
            </h1>
            <p className="text-sm text-text-muted-light dark:text-text-muted mt-1">
              Last updated: {currentDate}
            </p>
          </div>
          <DatabaseSelector />
        </div>
        <IconButton
          icon={ArrowPathIcon}
          label="Refresh Data"
          variant="primary"
          size="md"
          display="icon"
          onClick={fetchData}
        />
      </div>

      {/* ── Analytics ──────────────────────────────────── */}
      <div className="border-t border-border-light dark:border-border pt-8 mb-6">
        <h2 className="text-xl font-semibold text-text-primary-light dark:text-text-primary mb-4">
          {isDynamic ? (schema?.name || 'Analytics') : 'AI Adoption Analytics'}
        </h2>
      </div>
      {analyticsNoData && (
        <div className="bg-warning-light border border-warning p-6 rounded-lg mb-6">
          <h3 className="text-lg font-semibold text-warning-dark mb-2">No Survey Data Available</h3>
          <p className="text-text-primary-light dark:text-text-primary text-sm">
            Run <code className="bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded">npm run seed</code> in the backend directory to populate survey data.
          </p>
        </div>
      )}
      {error && (
        <div className="bg-danger-light border border-danger p-6 rounded-lg mb-6">
          <h3 className="text-lg font-semibold text-danger-dark mb-2">Error loading analytics</h3>
          <p className="text-sm text-text-primary-light dark:text-text-primary">{error?.message || 'Unknown error'}</p>
        </div>
      )}
      {loading && <div className="flex justify-center py-8"><LoadingSpinner /></div>}

      {analyticsReady && <>
      {/* Overview Cards */}
      {isDynamic ? (
        <Grid4 className="mb-6">
          <StatCard
            size="compact"
            title={
              <MetricWithTooltip
                label="Total Companies"
                definition={legacyMetricDefinitions.totalCompanies}
              />
            }
            value={analytics.totalCompanies}
            subtitle={`${formatNumber(analytics.totalSurveyResponses)} responses`}
          />
          {(layout?.overviewCards || []).slice(0, 3).map(card => {
            const def = metricLookup[card.metricKey];
            if (!def) return null;
            const value = analytics.metrics?.[card.metricKey];
            return (
              <StatCard
                key={card.metricKey}
                size="compact"
                title={
                  <MetricWithTooltip
                    label={def.label}
                    definition={metricDefinitions[card.metricKey] || def.label}
                  />
                }
                value={formatMetricValue(value, def)}
              />
            );
          })}
        </Grid4>
      ) : (
        <Grid4 className="mb-6">
          <StatCard
            size="compact"
            title={
              <MetricWithTooltip
                label="Total Companies"
                definition={metricDefinitions.totalCompanies}
              />
            }
            value={analytics.totalCompanies}
            subtitle={`${formatNumber(
              analytics.totalSurveyResponses
            )} responses`}
          />

          <StatCard
            size="compact"
            title={
              <MetricWithTooltip
                label="Avg Adoption Rate"
                definition={metricDefinitions.avgAdoptionRate}
              />
            }
            value={`${analytics.adoption.averageAdoptionRate.toFixed(1)}%`}
            subtitle={`${analytics.adoption.highAdopters} high adopters`}
            valueColor="success"
          />

          <StatCard
            size="compact"
            title={
              <MetricWithTooltip
                label="Avg Tech Affinity"
                definition={metricDefinitions.avgATIScore}
              />
            }
            value={`${analytics.techAffinity.averageATIScore.toFixed(1)}/100`}
            subtitle={`${analytics.techAffinity.percentageHighAffinity.toFixed(0)}% high`}
            valueColor="purple"
          />

          <StatCard
            size="compact"
            title={
              <MetricWithTooltip
                label="Avg AI Knowledge"
                definition={metricDefinitions.avgAIKnowledge}
              />
            }
            value={`${analytics.knowledge.averageAIKnowledge.toFixed(2)}/5`}
            subtitle={`${analytics.knowledge.minKnowledge.toFixed(2)} - ${analytics.knowledge.maxKnowledge.toFixed(2)} range`}
            valueColor="blue"
          />
        </Grid4>
      )}

      {/* Metrics Summary Cards */}
      {isDynamic && analytics.metrics ? (
        <Grid2 className="mb-6">
          {(() => {
            const overviewKeys = new Set((layout?.overviewCards || []).map(c => c.metricKey));
            const comparisonKeys = layout?.comparisonMetrics || [];
            // Show comparison metrics that aren't already in overview cards; fall back to all non-overview metrics
            const remaining = comparisonKeys.length > 0
              ? comparisonKeys.filter(k => !overviewKeys.has(k))
              : schemaMetrics.map(m => m.key).filter(k => !overviewKeys.has(k));
            const mid = Math.ceil(remaining.length / 2);
            const col1 = remaining.slice(0, mid);
            const col2 = remaining.slice(mid);

            const renderColumn = (keys, title) => (
              <Card key={title}>
                <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary mb-3">
                  {title}
                </h2>
                <div className="space-y-2">
                  {keys.map(key => {
                    const def = metricLookup[key];
                    return (
                      <div key={key} className="flex justify-between items-center">
                        <span className="text-sm text-text-secondary-light dark:text-text-secondary">
                          {def?.label || key}
                        </span>
                        <span className="font-semibold text-text-primary-light dark:text-text-primary">
                          {formatMetricValue(analytics.metrics[key], def)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );

            return <>
              {col1.length > 0 && renderColumn(col1, 'Key Metrics')}
              {col2.length > 0 && renderColumn(col2, 'Additional Metrics')}
            </>;
          })()}
        </Grid2>
      ) : (
        <Grid2 className="mb-6">
          <Card>
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary mb-3">
              AI Adoption Metrics
            </h2>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary-light dark:text-text-secondary">
                  <MetricWithTooltip
                    label="Avg Adoption Level"
                    definition={metricDefinitions.avgAdoptionLevel}
                  />
                </span>
                <span className="font-semibold text-green-600 dark:text-success">
                  {analytics.adoption.averageAdoptionLevel.toFixed(1)}/100
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary-light dark:text-text-secondary">
                  <MetricWithTooltip
                    label="High Adopters (60%+)"
                    definition={metricDefinitions.highAdoptersPercent}
                  />
                </span>
                <span className="font-semibold text-green-600 dark:text-success">
                  {analytics.adoption.highAdopters} (
                  {analytics.adoption.percentageHighAdopters.toFixed(1)}%)
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary-light dark:text-text-secondary">
                  <MetricWithTooltip
                    label="Avg Responses/Company"
                    definition={metricDefinitions.avgResponsesPerCompany}
                  />
                </span>
                <span className="font-semibold text-text-primary-light dark:text-text-primary">
                  {analytics.averageResponsesPerCompany.toFixed(1)}
                </span>
              </div>
            </div>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary mb-3">
              AI Perceptions
            </h2>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary-light dark:text-text-secondary">
                  <MetricWithTooltip
                    label="Avg Optimism"
                    definition={metricDefinitions.avgOptimismScore}
                  />
                </span>
                <span className="font-semibold text-text-primary-light dark:text-text-primary">
                  {analytics.perceptions.averageOptimism.toFixed(1)}/10
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary-light dark:text-text-secondary">
                  <MetricWithTooltip
                    label="Avg AI Trust"
                    definition={metricDefinitions.avgAITrust}
                  />
                </span>
                <span className="font-semibold text-green-600 dark:text-success">
                  {analytics.perceptions.averageTrust.toFixed(1)}/10
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary-light dark:text-text-secondary">
                  <MetricWithTooltip
                    label="Avg AI Fear"
                    definition={metricDefinitions.avgAIFear}
                  />
                </span>
                <span className="font-semibold text-red-600 dark:text-danger">
                  {analytics.perceptions.averageFear.toFixed(1)}/10
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-text-secondary-light dark:text-text-secondary">
                  <MetricWithTooltip
                    label="Learning Interest"
                    definition={metricDefinitions.learningInterest}
                  />
                </span>
                <span className="font-semibold text-text-primary-light dark:text-text-primary">
                  {analytics.learning.averageLearningInterest.toFixed(1)}%
                </span>
              </div>
            </div>
          </Card>
        </Grid2>
      )}

      {/* Time Series Tabs */}
      <div className="mb-4 border-b border-border-light dark:border-border-dark">
        <div className="flex items-center space-x-1 -mb-px">
          {timeSeriesTabs.map((tab, index) => (
            <div key={tab.id} className="relative group">
              <button
                onClick={() => setActiveTimeSeriesTab(index)}
                className={`px-4 py-2 font-medium transition relative ${
                  activeTimeSeriesTab === index
                    ? 'text-brand-primary'
                    : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
                }`}
              >
                {tab.name}
                {activeTimeSeriesTab === index && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary" />
                )}
              </button>
              {timeSeriesTabs.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTimeSeriesTab(index);
                  }}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs hover:bg-red-600"
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <div className="ml-3">
            <IconButton
              icon={PlusIcon}
              label="Add Chart"
              variant="primary"
              size="sm"
              display="icon"
              onClick={addTimeSeriesTab}
            />
          </div>
        </div>
      </div>

      {/* Time Series Chart */}
      <div className="mb-6 flex flex-col gap-2">
        {/* Filters - full width above chart */}
        <Card padding="sm">
          <TimeSeriesFilters
            filters={timeSeriesFilters}
            onFilterChange={setTimeSeriesFilters}
            onMetricsChange={setActiveTimeSeriesMetrics}
            onShowStdDevChange={setShowStdDev}
            industries={uniqueIndustries}
            villages={uniqueVillages}
            regions={uniqueRegions}
            ministries={uniqueMinistries}
            activeMetrics={activeTimeSeriesMetrics}
            allMetrics={availableMetrics}
            showStdDev={showStdDev}
          />
        </Card>

        {/* Chart - full width below */}
        <Card padding="sm">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-base font-semibold text-text-primary-light dark:text-text-primary">
              Metrics Over Time
            </h2>
            <p className="text-xs text-text-muted-light dark:text-text-muted">
              {getFilterSummary()}
            </p>
          </div>

          {timeSeriesLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <TimeSeriesChart
              data={timeSeriesData}
              metrics={activeTimeSeriesMetrics}
              showResponseCount={true}
              showStdDev={showStdDev}
              metricConfigs={dynamicTimeSeriesConfigs}
            />
          )}
        </Card>
      </div>

      {/* MaxDiff Charts */}
      {databaseId && layout?.maxDiffCharts?.length > 0 && (
        <MaxDiffChartsSection
          databaseId={databaseId}
          charts={layout.maxDiffCharts}
        />
      )}

      {/* Tab Navigation */}
      <div className="border-b border-border-light dark:border-border-dark mb-6">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-1 font-medium transition relative ${
              activeTab === 'overview'
                ? 'text-brand-primary'
                : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
            }`}
          >
            Company Rankings
            {activeTab === 'overview' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('breakdown')}
            className={`pb-3 px-1 font-medium transition relative ${
              activeTab === 'breakdown'
                ? 'text-brand-primary'
                : 'text-text-secondary-light dark:text-text-secondary hover:opacity-70'
            }`}
          >
            Category Breakdown
            {activeTab === 'breakdown' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-primary" />
            )}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <Card>
          <h2 className="text-xl font-bold text-text-primary-light dark:text-text-primary mb-4">
            All Companies Ranked
          </h2>
          <FilterableTable
            data={companiesTableData}
            searchFields={['name', 'industry', 'village']}
            searchPlaceholder="Search by company name, industry, or village..."
            filters={[
              {
                key: 'industry',
                label: 'Industry',
                placeholder: 'All Industries',
                options: industries
              }
            ]}
            defaultItemsPerPage={10}
            columns={companyTableColumns}
          />
        </Card>
      )}

      {activeTab === 'breakdown' && (
        <div className="space-y-4">
          <Accordion title="Industry Analysis" defaultOpen={true}>
            <FilterableTable
              data={industryData.map(item => ({
                ...item,
                onClick: () => navigate(`/insights?view=industry&category=${encodeURIComponent(item.industry)}${databaseId ? `&db=${schema?.slug || ''}` : ''}`)
              }))}
              searchFields={['industry']}
              searchPlaceholder="Search industries..."
              showFilters={true}
              defaultItemsPerPage={10}
              columns={makeBreakdownColumns('industry', 'Industry')}
            />
          </Accordion>

          <Accordion title="Village Analysis">
            <FilterableTable
              data={villageData.map(item => ({
                ...item,
                onClick: () => navigate(`/insights?view=village&category=${encodeURIComponent(item.village)}${databaseId ? `&db=${schema?.slug || ''}` : ''}`)
              }))}
              searchFields={['village']}
              searchPlaceholder="Search villages..."
              showFilters={true}
              defaultItemsPerPage={10}
              columns={makeBreakdownColumns('village', 'Village')}
            />
          </Accordion>

          <Accordion title="Region Analysis">
            <FilterableTable
              data={regionData.map(item => ({
                ...item,
                onClick: () => navigate(`/insights?view=region&category=${encodeURIComponent(item.region)}${databaseId ? `&db=${schema?.slug || ''}` : ''}`)
              }))}
              searchFields={['region']}
              searchPlaceholder="Search regions..."
              showFilters={true}
              defaultItemsPerPage={10}
              columns={makeBreakdownColumns('region', 'Region')}
            />
          </Accordion>

          <Accordion title="Sector Analysis (Government vs Non-Government)">
            <FilterableTable
              data={sectorData.map(item => ({
                ...item,
                onClick: () => navigate(`/insights?view=sector&category=${encodeURIComponent(item.sector)}${databaseId ? `&db=${schema?.slug || ''}` : ''}`)
              }))}
              searchFields={['sector']}
              searchPlaceholder="Search sectors..."
              showFilters={false}
              showPagination={false}
              defaultItemsPerPage={10}
              columns={makeBreakdownColumns('sector', 'Sector')}
            />
          </Accordion>

          <Accordion title="Ministry Analysis">
            <FilterableTable
              data={ministryData.map(item => ({
                ...item,
                onClick: () => navigate(`/insights?view=ministry&category=${encodeURIComponent(item.ministry)}${databaseId ? `&db=${schema?.slug || ''}` : ''}`)
              }))}
              searchFields={['ministry']}
              searchPlaceholder="Search ministries..."
              showFilters={true}
              defaultItemsPerPage={10}
              columns={makeBreakdownColumns('ministry', 'Ministry')}
            />
          </Accordion>
        </div>
      )}
      </>}
    </div>

  </>
  );
}
